import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Film, Loader2, Check, Music, Sparkles, AlignVerticalJustifyCenter } from 'lucide-react';
import ReelTransitionPreview from './ReelTransitionPreview';

const STAGES = { queued: 'In coda: un altro video è in elaborazione', preparing: 'Preparo le scene e il titolo', rendering: 'Animo le foto (Ken Burns)', encoding: 'Unisco scene, transizioni e musica', uploading: 'Salvo il video' };
const TRANSITIONS = [
  { value: 'white', label: 'Dissolvenza bianca' },
  { value: 'black', label: 'Dissolvenza nera' },
  { value: 'crossfade', label: 'Dissolvenza incrociata', heavy: true },
  { value: 'slide', label: 'Scorrimento laterale', heavy: true },
  { value: 'none', label: 'Nessuna (taglio secco)' },
];
const TITLE_POSITIONS = [
  { value: 'top', label: 'In alto' },
  { value: 'center', label: 'Al centro' },
  { value: 'bottom', label: 'In basso' },
];

const ReelBuilderPanel = ({ mediaUrls, defaultHeadline = '', defaultSubline = '', onApply, onCancel }) => {
  const { API, getAuthHeaders } = useAuth();
  const base = API.replace('/api', '');
  const [headline, setHeadline] = useState(defaultHeadline);
  const [subline, setSubline] = useState(defaultSubline);
  const [tracks, setTracks] = useState([]);
  const [music, setMusic] = useState('generated');
  const [seconds, setSeconds] = useState('2.5');
  const [transition, setTransition] = useState('white');
  const [titlePosition, setTitlePosition] = useState('bottom');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [limits, setLimits] = useState({ max_reel_photos: 8, heavy_transitions_enabled: true, low_memory: false });
  const pollRef = useRef(null);
  const maxPhotos = limits.max_reel_photos || 8;
  const photoCount = Math.min(mediaUrls.length, maxPhotos);

  useEffect(() => {
    axios.get(`${API}/ai-social/music`, getAuthHeaders()).then(r => setTracks(r.data)).catch(() => {});
    axios.get(`${API}/ai-social/limits`, getAuthHeaders()).then(r => {
      setLimits(r.data);
      if (!r.data.heavy_transitions_enabled && (r.data.heavy_transitions || []).includes(transition)) setTransition('white');
    }).catch(() => {});
    // riprende un Reel lasciato in lavorazione (l'utente può chiudere la pagina)
    const pending = localStorage.getItem('reel_job_id');
    if (pending) {
      axios.get(`${API}/ai-social/jobs/${pending}`, getAuthHeaders()).then(r => {
        if (r.data.status === 'running') { setRunning(true); setProgress(r.data.progress || { percent: 0, stage: 'preparing' }); pollJob(pending); }
        else if (r.data.status === 'done' && r.data.result) { setResult(r.data.result); localStorage.removeItem('reel_job_id'); }
        else localStorage.removeItem('reel_job_id');
      }).catch(() => localStorage.removeItem('reel_job_id'));
    }
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, getAuthHeaders]);

  const pollJob = (jobId) => {
    clearInterval(pollRef.current);
    let deadline = Date.now() + 10 * 60 * 1000;
    let failures = 0;
    pollRef.current = setInterval(async () => {
      if (Date.now() > deadline) {
        clearInterval(pollRef.current); setRunning(false);
        toast.error('La creazione del Reel sta impiegando troppo tempo. Ti avvisiamo con una notifica quando è pronto.');
        return;
      }
      try {
        const j = await axios.get(`${API}/ai-social/jobs/${jobId}`, getAuthHeaders());
        if (j.data.progress) setProgress({ ...j.data.progress, queue_position: j.data.queue_position });
        if (j.data.progress?.stage === 'queued') deadline = Date.now() + 10 * 60 * 1000;
        if (j.data.status === 'done') {
          clearInterval(pollRef.current); setResult(j.data.result); setRunning(false);
          localStorage.removeItem('reel_job_id');
          toast.success('Reel pronto!');
        } else if (j.data.status === 'error') {
          clearInterval(pollRef.current); setRunning(false);
          localStorage.removeItem('reel_job_id');
          toast.error(j.data.error || 'Creazione Reel fallita');
        }
      } catch {
        failures += 1;
        if (failures >= 4) { clearInterval(pollRef.current); setRunning(false); toast.error('Errore nel recupero dello stato del Reel'); }
      }
    }, 2000);
  };

  const build = async () => {
    setRunning(true); setResult(null); setProgress({ percent: 0, stage: 'preparing' });
    try {
      const res = await axios.post(`${API}/ai-social/reel`, {
        media_urls: mediaUrls.slice(0, maxPhotos), headline: headline || null, subline: subline || null,
        music_url: music === 'generated' ? null : music, seconds_per_slide: Number(seconds),
        transition, title_position: titlePosition,
      }, getAuthHeaders());
      localStorage.setItem('reel_job_id', res.data.job_id);
      pollJob(res.data.job_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore avvio Reel');
      setRunning(false);
    }
  };

  return (
    <div className="rounded-lg border bg-muted/20 p-3 space-y-3" data-testid="reel-builder-panel">
      <div className="flex items-center gap-2 text-sm font-medium"><Film className="w-4 h-4 text-primary" /> Reel automatico da {photoCount} foto</div>
      {limits.low_memory && (
        <p className="text-xs text-muted-foreground" data-testid="reel-limits-note">
          Piano risorse ridotto ({limits.memory_limit_mb} MB): massimo {maxPhotos} foto per Reel e transizioni
          scorrimento/dissolvenza incrociata non disponibili. I video vengono elaborati uno alla volta.
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Titolo (prima scena)</Label>
              <Input value={headline} onChange={e => setHeadline(e.target.value)} maxLength={60} placeholder="es. Nuovi colori autunno" data-testid="reel-headline-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Sottotitolo</Label>
              <Input value={subline} onChange={e => setSubline(e.target.value)} maxLength={80} placeholder="es. Prenota ora" data-testid="reel-subline-input" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1"><Music className="w-3 h-3" /> Musica</Label>
              <Select value={music} onValueChange={setMusic}>
                <SelectTrigger data-testid="reel-music-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="generated">Ambient soft (generata, senza diritti)</SelectItem>
                  {tracks.map(t => <SelectItem key={t.id} value={t.media_url}>{t.title}</SelectItem>)}
                  <SelectItem value="none">Nessuna musica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Durata per foto</Label>
              <Select value={seconds} onValueChange={setSeconds}>
                <SelectTrigger data-testid="reel-seconds-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 secondi</SelectItem>
                  <SelectItem value="2.5">2,5 secondi</SelectItem>
                  <SelectItem value="3.5">3,5 secondi</SelectItem>
                  <SelectItem value="5">5 secondi</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1"><Sparkles className="w-3 h-3" /> Transizione</Label>
              <Select value={transition} onValueChange={setTransition}>
                <SelectTrigger data-testid="reel-transition-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TRANSITIONS.map(t => {
                    const blocked = t.heavy && !limits.heavy_transitions_enabled;
                    return (
                      <SelectItem key={t.value} value={t.value} disabled={blocked} data-testid={`reel-transition-${t.value}`}>
                        {t.label}{blocked ? ' — richiede più memoria' : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1"><AlignVerticalJustifyCenter className="w-3 h-3" /> Posizione titolo</Label>
              <Select value={titlePosition} onValueChange={setTitlePosition}>
                <SelectTrigger data-testid="reel-title-position-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TITLE_POSITIONS.map(t => <SelectItem key={t.value} value={t.value} data-testid={`reel-title-position-${t.value}`}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <ReelTransitionPreview transition={transition} titlePosition={titlePosition} />
          </div>
          {running && progress && (
            <div className="space-y-1" data-testid="reel-progress">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{STAGES[progress.stage] || 'Elaborazione...'}{progress.stage === 'queued' && progress.queue_position > 1 ? ` (posizione ${progress.queue_position})` : ''}</span>
                <span data-testid="reel-progress-percent">{progress.percent}%</span>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-primary transition-all duration-500" style={{ width: `${progress.percent}%` }} />
              </div>
              <p className="text-xs text-muted-foreground" data-testid="reel-background-hint">
                Puoi chiudere questa finestra: ti avvisiamo con una notifica in app quando il Reel è pronto.
              </p>
            </div>
          )}
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={build} disabled={running} data-testid="reel-build-btn">
              {running ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Film className="w-3.5 h-3.5 mr-1" />} {running ? 'Creazione in corso...' : result ? 'Rigenera Reel' : 'Crea Reel'}
            </Button>
            {result && <Button size="sm" onClick={() => onApply(result.url, result.thumbnail_url)} data-testid="reel-apply-btn"><Check className="w-3.5 h-3.5 mr-1" /> Usa questo Reel</Button>}
            {onCancel && <Button size="sm" variant="ghost" onClick={onCancel} data-testid="reel-cancel-btn">Annulla</Button>}
          </div>
        </div>
        <div className="w-[120px] h-[213px] rounded-md overflow-hidden border bg-black mx-auto flex items-center justify-center">
          {result
            ? <video src={`${base}${result.url}`} poster={`${base}${result.thumbnail_url}`} controls muted className="w-full h-full object-cover" data-testid="reel-preview-video" />
            : <img src={`${base}${mediaUrls[0]}`} alt="" className="w-full h-full object-cover opacity-60" />}
        </div>
      </div>
    </div>
  );
};

export default ReelBuilderPanel;
